/**
 * Utilities for building entity URLs in audit logs.
 */

/**
 * Build URL for entity based on type and ID.
 * Uses window.location.origin to support local, standalone, and production environments.
 *
 * @param objectType - The type of entity (Campaign, Newsletter, TransactionalMessage, Segment)
 * @param objectId - The ID of the entity
 * @param contextId - The workspace ID (optional, but required for URL generation)
 * @returns The entity URL or null if parameters are invalid
 */
export function buildEntityUrl(
  objectType: string,
  objectId: string | number | null,
  contextId?: number,
): string | null {
  if (objectId == null || contextId == null || contextId === 0) {
    return null;
  }

  const id = typeof objectId === 'string' ? objectId : String(objectId);
  // Use window.location.origin to support localhost, standalone, and production
  const baseUrl = `${window.location.origin}/workspaces/${contextId}/journeys`;

  switch (objectType) {
    case 'Campaign':
      return `${baseUrl}/campaigns/${id}/setup/workflow/actions`;
    case 'Newsletter':
      return `${baseUrl}/broadcasts/newsletter/${id}/overview/reports`;
    case 'TransactionalMessage':
      return `${baseUrl}/transactional/${id}/`;
    case 'Segment':
      return `${baseUrl}/segments/${id}/overview`;
    default:
      return null;
  }
}

/**
 * Get entity type label for display in links (e.g., "See campaign").
 *
 * @param objectType - The type of entity
 * @returns A human-readable label for the entity type
 */
export function getEntityTypeLabel(objectType: string): string {
  switch (objectType) {
    case 'Campaign':
      return 'campaign';
    case 'Newsletter':
      return 'newsletter';
    case 'TransactionalMessage':
      return 'transactional message';
    case 'Segment':
      return 'segment';
    default:
      return 'entity';
  }
}
